import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { getRouteConfig } from "../config/routeConfig";
import UnauthorizedAccess from "../views/404/UnauthorizedAccess";

/**
 * `strict` drops the "super_admin / admin see everything" bypass, so the route
 * is decided purely by its configured roles and permissions. Opt in only where
 * a route must exclude the admin role.
 */
const ProtectedRoute = ({
  children,
  roles = [],
  permissions = [],
  strict = false,
}) => {
  const location = useLocation();

  const userRoles = useSelector((state) => state.user.userInfo.user_roles);
  const userPermissions = useSelector(
    (state) => state.user.userInfo.permissions
  );
  const userInfoLoading = useSelector((state) => state.user.userInfoLoading);

  // Check if user has required role
  const hasRole = (userRoles, requiredRoles) => {
    if (!requiredRoles || requiredRoles.length === 0) return true;
    return requiredRoles.some((role) => userRoles.includes(role));
  };

  // Check if user has required permission
  const hasPermission = (userPermissions, requiredPermissions) => {
    if (!requiredPermissions || requiredPermissions.length === 0) return true;
    return requiredPermissions.some((permission) =>
      userPermissions.some(
        (userPermission) => userPermission.name === permission
      )
    );
  };

  // Check if user is super admin or admin (they have access to everything except POS routes)
  const isSuperUser = (userRoles) => {
    return userRoles.includes("super_admin") || userRoles.includes("admin");
  };

  useEffect(() => {
    // This effect can be used for logging or analytics
    // The actual authorization is handled in the render logic
    if (userInfoLoading) return;

    // Log unauthorized access attempts in development
    if (process.env.NODE_ENV === "development") {
      const routeConfig = getRouteConfig(location.pathname);
      const requiredRoles = roles.length > 0 ? roles : routeConfig.roles;
      const requiredPermissions =
        permissions.length > 0 ? permissions : routeConfig.permissions;

      const hasRequiredRole = hasRole(userRoles, requiredRoles);
      const hasRequiredPermission = hasPermission(
        userPermissions,
        requiredPermissions
      );
      const isSuper = isSuperUser(userRoles);

      if (!isSuper && !hasRequiredRole && !hasRequiredPermission) {
        console.warn(`Unauthorized access attempt to ${location.pathname}`, {
          userRoles,
          requiredRoles,
          requiredPermissions,
        });
      }
    }
  }, [
    userRoles,
    userPermissions,
    userInfoLoading,
    location.pathname,
    roles,
    permissions,
  ]);

  // Don't render anything while loading user info
  if (userInfoLoading) {
    return null;
  }

  // Don't render if no user roles
  if (!userRoles || userRoles.length === 0) {
    return null;
  }

  // Get route configuration for current path
  const routeConfig = getRouteConfig(location.pathname);
  const requiredRoles = roles.length > 0 ? roles : routeConfig.roles;
  const requiredPermissions =
    permissions.length > 0 ? permissions : routeConfig.permissions;

  const hasRequiredRole = hasRole(userRoles, requiredRoles);
  const hasRequiredPermission = hasPermission(
    userPermissions,
    requiredPermissions
  );
  const isSuper = isSuperUser(userRoles);

  // Special case for POS routes - only POS users can access
  if (location.pathname.startsWith("/pos/")) {
    if (userRoles.includes("pos")) {
      return children;
    }
    return <UnauthorizedAccess />;
  }

  // For non-POS routes, check normal permissions
  if ((isSuper && !strict) || hasRequiredRole || hasRequiredPermission) {
    return children;
  }

  // Return UnauthorizedAccess if no access
  return <UnauthorizedAccess />;
};

export default ProtectedRoute;
